import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import styled from "styled-components";
import { fetchDevices, revokeDevice } from "./devices.redux";
import moment from "moment";

const Container = styled.div`
  width: 100%;
  max-width: 700px;
  padding: 2rem 1rem;
  margin: 0 auto;
`;

const Title = styled.h2`
  font: normal normal 600 20px/30px Poppins;
  color: #333;
  margin-bottom: 1.5rem;
`;

const DeviceCard = styled.div`
  background: #ffffff;
  border: 1px solid #d4d4d5;
  border-radius: 4px;
  padding: 1rem 1.25rem;
  margin-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;

  ${(props) =>
    props.$current &&
    `
    border-left: 4px solid #00297a;
  `}
`;

const DeviceInfo = styled.div`
  flex: 1;
  min-width: 200px;
`;

const DeviceName = styled.div`
  font: normal normal 600 14px/20px Poppins;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CurrentBadge = styled.span`
  background: #00297a;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
`;

const DeviceMeta = styled.div`
  font: normal normal normal 12px/18px Poppins;
  color: #707070;
  margin-top: 4px;
`;

const RevokeButton = styled.button`
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font: normal normal 600 12px/18px Poppins;
  cursor: pointer;
  &:hover {
    background: #c0392b;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: #707070;
  padding: 3rem 1rem;
  font: normal normal normal 14px/20px Poppins;
`;

const LoadingState = styled.div`
  text-align: center;
  color: #707070;
  padding: 3rem 1rem;
`;

const DevicesScreenComponent = ({
  devices,
  loading,
  revoking,
  fetchDevices,
  revokeDevice,
}) => {
  const { t } = useTranslation();
  const [confirmId, setConfirmId] = useState(null);
  const currentDeviceId = localStorage.getItem("device_id");

  useEffect(() => {
    fetchDevices();
  }, []);

  const handleRevoke = (deviceId) => {
    revokeDevice({ deviceId });
    setConfirmId(null);
  };

  if (loading && devices.length === 0) {
    return (
      <Container>
        <Title>Connected Devices</Title>
        <LoadingState>Loading...</LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Connected Devices</Title>
      {devices.length === 0 ? (
        <EmptyState>No devices found.</EmptyState>
      ) : (
        devices.map((device) => {
          const isCurrent = device.device_id === currentDeviceId;
          const lastActive = device.last_active_at
            ? moment(device.last_active_at).fromNow()
            : "Unknown";

          return (
            <DeviceCard key={device.id} $current={isCurrent}>
              <DeviceInfo>
                <DeviceName>
                  {device.device_name || device.device_id || "Unknown Device"}
                  {isCurrent && <CurrentBadge>This device</CurrentBadge>}
                </DeviceName>
                <DeviceMeta>
                  Last active: {lastActive}
                  {device.last_ip && ` · IP: ${device.last_ip}`}
                </DeviceMeta>
              </DeviceInfo>
              {!isCurrent && (
                <>
                  {confirmId === device.id ? (
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <RevokeButton
                        onClick={() => handleRevoke(device.id)}
                        disabled={revoking === device.id}
                      >
                        {revoking === device.id ? "..." : "Confirm"}
                      </RevokeButton>
                      <RevokeButton
                        style={{ background: "#aaa" }}
                        onClick={() => setConfirmId(null)}
                      >
                        Cancel
                      </RevokeButton>
                    </div>
                  ) : (
                    <RevokeButton onClick={() => setConfirmId(device.id)}>
                      Disconnect
                    </RevokeButton>
                  )}
                </>
              )}
            </DeviceCard>
          );
        })
      )}
    </Container>
  );
};

export const DevicesScreen = connect(
  (state) => ({
    devices: state.devices.devices,
    loading: state.devices.loading,
    revoking: state.devices.revoking,
  }),
  { fetchDevices, revokeDevice },
)(DevicesScreenComponent);
